import * as React from 'react';
import { useState } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  IconButton,
  Typography,
  CloseIcon,
} from '@execonline-inc/execonline-ui';
import { DashboardSummaryPayload } from '../../../DashboardSummaryStore/Types';
import SummaryTabContent from './SummaryTabContent';
import FullAnalysisTabContent from './FullAnalysisTabContent';
import SparklesIcon from '../../Cutesies/SparklesIcon';
import { T } from '../../../Translations';

type TabType = 'summary' | 'full-analysis';

interface Props {
  open: boolean;
  onClose: () => void;
  summary: DashboardSummaryPayload | null;
  isLoading: boolean;
  error: string | null;
  emptyMessage?: string | null;
  onRefresh?: () => void;
}

const AIAnalysisDrawer: React.FC<Props> = ({
  open,
  onClose,
  summary,
  isLoading,
  error,
  emptyMessage,
  onRefresh,
}) => {
  // Default to 'full-analysis' as per requirements
  const [activeTab, setActiveTab] = useState<TabType>('full-analysis');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      className="!w-[650px]"
      data-intercom-target="ai-analysis-drawer"
    >
      <DrawerHeader className="flex-col items-start gap-2 !py-4">
        {/* Close button */}
        <IconButton
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
          onClick={onClose}
          data-intercom-target="ai-analysis-drawer-close-button"
        >
          <CloseIcon />
        </IconButton>

        {/* Title with sparkles icon */}
        <div className="flex items-center space-x-2 pr-8">
          <SparklesIcon className="w-5 h-5" color="#3b8860" />
          <Typography variant="bodyMedium" fontWeight="semibold" color="dark">
            <T kind="AI Action Plan Analysis" />
          </Typography>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 w-full mt-2">
          <button
            className={`px-4 py-2 text-sm leading-5 rounded-md transition-colors ${
              activeTab === 'summary'
                ? 'font-semibold bg-teal-600 text-white'
                : 'font-normal bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
            onClick={() => setActiveTab('summary')}
            data-intercom-target="ai-analysis-summary-tab"
          >
            <T kind="Summary" />
          </button>
          <button
            className={`px-4 py-2 text-sm leading-5 rounded-md transition-colors ${
              activeTab === 'full-analysis'
                ? 'font-semibold bg-teal-600 text-white'
                : 'font-normal bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
            onClick={() => setActiveTab('full-analysis')}
            data-intercom-target="ai-analysis-full-analysis-tab"
          >
            <T kind="Full Analysis" />
          </button>
        </div>
      </DrawerHeader>

      <DrawerBody className="flex-col p-6">
        {activeTab === 'summary' ? (
          <SummaryTabContent
            summary={summary}
            isLoading={isLoading}
            error={error}
            emptyMessage={emptyMessage}
            onRefresh={onRefresh}
          />
        ) : (
          <FullAnalysisTabContent
            summary={summary}
            isLoading={isLoading}
            error={error}
            emptyMessage={emptyMessage}
            onRefresh={onRefresh}
          />
        )}
      </DrawerBody>
    </Drawer>
  );
};

export default AIAnalysisDrawer;
